import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Settings, Clock, Sparkles, CheckCircle2 } from "lucide-react";
import logoSvg from "@/assets/logo.svg";
import { AuthModal } from "@/components/auth/AuthModal";
import { CookieConsent } from "@/components/common/CookieConsent";
import { trackPageView } from "@/lib/analytics";

interface MaintenancePageProps {
  message?: string | null;
  estimatedEnd?: string | null;
}

/**
 * MaintenancePage - Visas när full_maintenance_mode är aktivt
 *
 * En premium underhållssida som informerar besökare om att
 * systemet tillfälligt är stängt för underhåll.
 *
 * Administratörer kan logga in via ?admin=1 i URL:en
 * eller genom att klicka på © i footern
 */
export function MaintenancePage({ message, estimatedEnd }: MaintenancePageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Kolla om admin-parametern finns i URL:en
  const isAdminLogin = searchParams.get("admin") === "1" || searchParams.get("login") === "1";

  // Öppna login-modalen automatiskt om admin-parametern finns
  useEffect(() => {
    if (isAdminLogin) {
      setShowLoginModal(true);
    }
  }, [isAdminLogin]);

  // Track maintenance page view
  useEffect(() => {
    trackPageView("/maintenance");
  }, []);

  // Beräkna återstående tid om estimatedEnd finns
  useEffect(() => {
    if (!estimatedEnd) {
      setTimeRemaining(null);
      return;
    }

    const updateTimeRemaining = () => {
      const endTime = new Date(estimatedEnd);
      const now = new Date();
      const diff = endTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}min`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes} minuter`);
      } else {
        setTimeRemaining("Strax klart");
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [estimatedEnd]);

  const handleAuthSuccess = () => {
    navigate("/dashboard");
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
    if (isAdminLogin) {
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <Helmet>
        <title>Kvanten</title>
      </Helmet>

      {/* Background decoration - matching landing page style */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary-100/40 via-blue-100/30 to-emerald-100/40 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-l from-emerald-100/30 to-primary-100/20 rounded-full blur-3xl opacity-40" />
      </div>

      {/* Header with logo */}
      <header className="relative z-10 pt-8 pb-4 px-4">
        <div className="max-w-7xl mx-auto flex justify-center">
          <img
            src={logoSvg}
            alt="Kvanten"
            className="h-8 w-auto"
          />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 relative z-10">
        <div className="max-w-2xl w-full text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-50 to-emerald-50 border border-primary-100 text-primary-700 rounded-full text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            <span>Planerat underhåll</span>
          </div>

          {/* Icon with decorative background */}
          <div className="relative mb-8">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center shadow-xl shadow-primary-500/25">
              <Settings className="w-12 h-12 text-white animate-[spin_8s_linear_infinite]" />
            </div>
            {/* Decorative ring */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 border-2 border-primary-200 rounded-[2rem] opacity-60" />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
            Vi förbättrar din upplevelse
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-600 max-w-xl mx-auto leading-relaxed mb-10">
            {message ||
              "Just nu genomför vi planerat underhåll för att göra Kvanten ännu bättre. Vi är snart tillbaka."}
          </p>

          {/* Time estimate card */}
          {(timeRemaining || estimatedEnd) && (
            <div className="inline-flex items-center gap-4 px-6 py-4 bg-white rounded-2xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 font-medium">Beräknad återgång</p>
                <p className="text-lg font-semibold text-gray-900">
                  {timeRemaining ? (
                    <>Om {timeRemaining}</>
                  ) : estimatedEnd ? (
                    <>
                      {new Date(estimatedEnd).toLocaleString("sv-SE", {
                        weekday: "long",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          )}

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>All data är säker</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span>Inga åtgärder krävs</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - © triggers admin login */}
      <footer className="relative z-10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-4">
            {/* Thank you message */}
            <p className="text-sm text-gray-500">
              Tack för ditt tålamod
            </p>

            {/* Copyright with hidden login trigger */}
            <p className="text-sm text-gray-400">
              <button
                onClick={() => setShowLoginModal(true)}
                className="hover:text-gray-600 transition-colors"
              >
                ©
              </button>
              {" "}{new Date().getFullYear()} Kvanten. Alla rättigheter förbehållna.
            </p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <AuthModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        initialMode="login"
        onAuthSuccess={handleAuthSuccess}
        navigateAfterLogin={false}
      />

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}
